import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Collapse, Grid, Tooltip, alpha, useTheme } from '@mui/material'
import { isBefore, isSameDay, startOfDay, differenceInDays, isAfter } from 'date-fns'
import { capitalize } from 'lodash'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useFormContext, useWatch, type Control, type FieldValues, type UseFormReturn } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { useLazyFetchConfiguredServiceEventsQuery } from 'api/configured-service/configuredServiceCrud'
import { ReactComponent as RecurrenceIcon } from 'assets/Recurrence.svg'
import { HHAlert } from 'components/common/HHAlert'
import RecurrenceDisplayList from 'components/configured-service/accordions/ServiceDetailsAccordion/components/RecurrenceDisplayList'
import ServiceDetailsViewItemBase from 'components/configured-service/accordions/ServiceDetailsAccordion/ServiceDetailsViewContent/ServiceDetailsViewItem'
import EditServiceRecurrence from 'components/configured-service/EditServiceRecurrenceDialog/EditServiceRecurrence'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import {
  HHFormDesktopDatePicker as HHFormDesktopDatePickerBase,
  HHFormTextField,
  HHFormAutocompleteField as HHFormAutocompleteFieldBase,
  HHFormPriceField as HHFormPriceFieldBase,
  HHFormSelectField as HHFormSelectFieldBase,
  HHFormMuiSwitch as HHFormMuiSwitchBase,
} from 'components/form-fields/v5'
import HHDesktopDatePicker from 'components/form-fields/v5/HHDesktopDatePicker'
import { getPricingLabel } from 'components/pricing/pricingMethods'
import ActionsAutocompleteFormFieldBase from 'components/pricing/Services/edit-service/ActionsAutocompleteFormField'
import MaterialsAutocompleteFormFieldBase from 'components/pricing/Services/edit-service/MaterialsAutocompleteFormField'
import MeasuresAutocompleteFormFieldBase from 'components/pricing/Services/edit-service/MeasuresAutocompleteFormField'
import MethodsAutocompleteFormFieldBase from 'components/pricing/Services/edit-service/MethodsAutocompleteFormField'
import PricingZoneAutocompleteFormFieldBase from 'components/pricing/Services/edit-service/PricingZoneAutocompleteFormField'
import {
  selectIsOnRequestService,
  selectPricedServiceRecurrenceText,
  selectScheduledServiceEvents,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import {
  selectMeasures,
  selectMethods,
  selectMaterials,
  selectServiceActions,
  selectPricingPeriods,
  selectPricingZones,
  selectRecurrenceOptions,
  selectTaxZoneOptions,
} from 'data/meta/metaSelectors'
import { selectIsLegacyTaxesEnabled, selectIsNewTaxesEnabled } from 'data/tenant/tenantSettingsSelectors'
import { RECURRENCE_INTERVALS } from 'settings/constants/billing'
import { RECURRENCE, RECURRENCE_V2, TERMINAL_WORK_ORDER_STATUSES } from 'settings/constants/service'
import T from 'T'
import { createDateFromString } from 'utils/date'
import { getRecurrenceText, isNonWeeklyRecurrence as getIsNonWeeklyRecurrence } from 'utils/service'

import { useAdjustService } from './AdjustServiceContext'
import AdjustServiceSectionHeaderBase from './AdjustServiceSectionHeader'
import ServiceActiveSwitch from './ServiceActiveSwitch'

// Untyped/out-of-scope form-field children — cast at the module boundary so this .tsx can render them.
const HHFormDesktopDatePicker = HHFormDesktopDatePickerBase as React.ElementType
const HHFormAutocompleteField = HHFormAutocompleteFieldBase as React.ElementType
const HHFormPriceField = HHFormPriceFieldBase as React.ElementType
const HHFormSelectField = HHFormSelectFieldBase as React.ElementType
const HHFormMuiSwitch = HHFormMuiSwitchBase as React.ElementType
const MeasuresAutocompleteFormField = MeasuresAutocompleteFormFieldBase as React.ElementType
const MaterialsAutocompleteFormField = MaterialsAutocompleteFormFieldBase as React.ElementType
const MethodsAutocompleteFormField = MethodsAutocompleteFormFieldBase as React.ElementType
const ActionsAutocompleteFormField = ActionsAutocompleteFormFieldBase as React.ElementType
const PricingZoneAutocompleteFormField = PricingZoneAutocompleteFormFieldBase as React.ElementType
const ServiceDetailsViewItem = ServiceDetailsViewItemBase as React.ElementType
const AdjustServiceSectionHeader = AdjustServiceSectionHeaderBase as React.ElementType

const getSimpleOptionLabel = (o: string | number) => `${o}x`
const getSimpleOptionValue = (v: string | number) => v
const getTaxZoneOptionLabel = ({ label }: { label?: string }) => label
const getRecurrenceOptionLabel = (option: { recurrencePer?: string | null; recurrenceInterval?: string | null }) =>
  getRecurrenceText(option.recurrencePer, option.recurrenceInterval)
const isRecurrenceOptionEqual = (option: { id?: unknown }, value: { id?: unknown }) => option.id === value.id

type ServiceAttributeFieldsProps = {
  measureList: unknown[]
  materialList: unknown[]
  methodList: unknown[]
  actionList: unknown[]
  canEdit: boolean
  fieldSize: { xs: number; lg: number }
  control: Control<FieldValues>
}

const ServiceAttributeFields = ({
  measureList,
  materialList,
  methodList,
  actionList,
  canEdit,
  fieldSize,
  control,
}: ServiceAttributeFieldsProps) => (
  <>
    <Grid size={fieldSize}>
      <MeasuresAutocompleteFormField control={control} disabled={!canEdit} name="measure" options={measureList} />
    </Grid>
    <Grid size={fieldSize}>
      <MaterialsAutocompleteFormField control={control} disabled={!canEdit} name="material" options={materialList} />
    </Grid>
    <Grid size={fieldSize}>
      <MethodsAutocompleteFormField control={control} disabled={!canEdit} name="method" options={methodList} />
    </Grid>
    <Grid size={fieldSize}>
      <ActionsAutocompleteFormField control={control} disabled={!canEdit} name="action" options={actionList} />
    </Grid>
  </>
)

const AdjustServiceFormContent = ({ serviceId }: { serviceId: string }) => {
  const theme = useTheme()
  const { data } = useCSDetailsCachedData({ id: serviceId })
  const {
    metaFiltersData,
    canEdit,
    canEditPrice,
    isDraft,
    isRecurrencePublishable,
    recurrencePublishBlockerMessage,
    recurrencePublishBlockerReason,
    handleRecurrenceUpdated,
  } = useAdjustService() as {
    metaFiltersData: unknown
    canEdit: boolean
    canEditPrice: boolean
    isDraft: boolean
    isRecurrencePublishable: boolean
    recurrencePublishBlockerMessage: string
    recurrencePublishBlockerReason: string
    handleRecurrenceUpdated: () => void
  }
  const showRecurrenceEditAlert = isDraft && !isRecurrencePublishable && recurrencePublishBlockerReason === 'recurrence'

  const [fetchCSEvents, { data: eventsData }] = useLazyFetchConfiguredServiceEventsQuery()

  const measureList = useMemo(() => selectMeasures(metaFiltersData), [metaFiltersData])
  const methodList = useMemo(() => selectMethods(metaFiltersData), [metaFiltersData])
  const materialList = useMemo(() => selectMaterials(metaFiltersData), [metaFiltersData])
  const actionList = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])
  const pricingPeriods = useMemo(() => selectPricingPeriods(metaFiltersData), [metaFiltersData])

  // Instance pricing is allowed for recurring services — it enables event-based (per-occurrence) pricing.
  const pricingPeriodList = pricingPeriods
  const pricingZoneList = useMemo(() => selectPricingZones(metaFiltersData), [metaFiltersData])
  const recurrenceList = useMemo(() => selectRecurrenceOptions(metaFiltersData), [metaFiltersData])
  const taxZoneList = useMemo(() => selectTaxZoneOptions(metaFiltersData), [metaFiltersData])
  const isNewTaxesEnabled = useSelector(selectIsNewTaxesEnabled)
  const isLegacyTaxesEnabled = useSelector(selectIsLegacyTaxesEnabled)

  const isOnRequestService = useMemo(() => selectIsOnRequestService(data), [data])
  const recurrenceText = useMemo(() => selectPricedServiceRecurrenceText(data), [data])
  const scheduledEvents = useMemo(
    () => selectScheduledServiceEvents({ events: eventsData, actions: actionList, isDraft }),
    [eventsData, actionList, isDraft]
  )

  useEffect(() => {
    if (serviceId) fetchCSEvents({ id: serviceId })
  }, [serviceId, fetchCSEvents])

  const {
    register,
    unregister,
    setValue,
    control,
    watch,
    formState: { errors, dirtyFields },
    getValues,
    resetField,
  } = useFormContext()

  const watchedStartDate = useWatch({ control, name: 'startDate' })
  const watchedEndDate = useWatch({ control, name: 'endDate' })
  const watchedServiceType = useWatch({ control, name: 'serviceType' })
  const watchedRecurrence = useWatch({ control, name: 'recurrence' })
  const useDifferentDatesForBilling = useWatch({ control, name: 'useDifferentDatesForBilling' })
  const useBillingName = useWatch({ control, name: 'useBillingName' })
  const isNonWeeklyRecurrence = watchedRecurrence && getIsNonWeeklyRecurrence(watchedRecurrence.recurrenceInterval)
  const isRecurringService = useMemo(
    () => watchedServiceType === RECURRENCE_V2.RECURRING || watchedServiceType === RECURRENCE.RECURRING,
    [watchedServiceType]
  )
  const deactivateError = errors?.deactivate?.message
  const startDateError = errors?.startDate?.message

  useEffect(() => {
    if (isRecurringService && isNonWeeklyRecurrence) {
      register('eventInstanceType', { required: T.THIS_FIELD_IS_REQUIRED, shouldUnregister: true })
    } else {
      unregister('eventInstanceType')
    }
  }, [isRecurringService, isNonWeeklyRecurrence, register, unregister])

  // Shared factory for date range validators
  const createShouldDisableBeforeDate = useCallback(
    (compareField: string) => (date: Date) => {
      const dateToCompare = getValues(compareField)
      if (!dateToCompare) return false
      return !isBefore(date, dateToCompare) && !isSameDay(date, dateToCompare)
    },
    [getValues]
  )

  const createShouldDisableAfterDate = useCallback(
    (compareField: string) => (date: Date) => {
      const dateToCompare = getValues(compareField)
      if (!dateToCompare) return false
      return !isAfter(date, dateToCompare) && !isSameDay(date, dateToCompare)
    },
    [getValues]
  )

  const shouldDisableStartDate = useMemo(() => createShouldDisableBeforeDate('endDate'), [createShouldDisableBeforeDate])
  const shouldDisableEndDate = useMemo(() => createShouldDisableAfterDate('startDate'), [createShouldDisableAfterDate])
  const shouldDisableBillingStartDate = useMemo(() => createShouldDisableBeforeDate('billingEndDate'), [createShouldDisableBeforeDate])
  const shouldDisableBillingEndDate = useMemo(() => createShouldDisableAfterDate('billingStartDate'), [createShouldDisableAfterDate])

  // Shared end-date setter logic for deactivate toggles
  const handleEndDateToggle = useCallback(
    (checked: boolean, requiresRecurring = false) => {
      if (requiresRecurring && !isRecurringService) return
      if (checked && !watch('endDate')) {
        const startDate = watch('startDate')
        const minEndDate = startDate ? new Date(startDate) : new Date()
        const today = new Date()
        setValue('endDate', isAfter(today, minEndDate) || isSameDay(today, minEndDate) ? today : minEndDate)
      } else if (!checked && !dirtyFields.endDate) {
        resetField('endDate')
      }
    },
    [watch, setValue, resetField, dirtyFields.endDate, isRecurringService]
  )

  const handleDeactivateOnEndDateChange = useCallback((checked: boolean) => handleEndDateToggle(checked, false), [handleEndDateToggle])

  const handleDeactivateChange = useCallback((checked: boolean) => handleEndDateToggle(checked, true), [handleEndDateToggle])

  const onDifferentBillingDatesChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        const currentBillingStartDate = watch('billingStartDate')
        const currentBillingEndDate = watch('billingEndDate')
        if (!currentBillingStartDate && !currentBillingEndDate) {
          setValue('billingStartDate', watch('startDate'))
          setValue('billingEndDate', watch('endDate'))
        }
      } else {
        setValue('billingStartDate', watch('startDate'))
        setValue('billingEndDate', watch('endDate'))
      }
    },
    [watch, setValue]
  )

  const onUseBillingNameChange = useCallback(
    (checked: boolean) => {
      if (!checked) setValue('billingName', '')
    },
    [setValue]
  )

  const validateDeactivateField = useCallback(
    (value: unknown) => {
      if (!value) return true
      const { endDate } = getValues()
      const hasFutureEvents = scheduledEvents
        .filter(event => event.configuredServiceEventType !== 'Recurring')
        .filter(event => !TERMINAL_WORK_ORDER_STATUSES.includes(event.workOrderStatus ?? ''))
        .map(({ serviceDate }) => createDateFromString(serviceDate ?? ''))
        .filter((date): date is Date => Boolean(date))
        .some(date => !isSameDay(date, endDate) && isAfter(startOfDay(date), endDate))
      if (hasFutureEvents) return T.ON_REQUEST_DEACTIVATE
      const difference = differenceInDays(endDate, new Date())
      if (difference > 0) return T.ON_RECCURING_DEACTIVATE_FUTURE_DATE_MSG
      return undefined
    },
    [getValues, scheduledEvents]
  )

  const validateStartDateField = useCallback((date: Date | null) => {
    if (!date) return T.THIS_FIELD_IS_REQUIRED
    return undefined
  }, [])

  const handleRecurrenceUpdate = useCallback(
    (updateData: Record<string, unknown>) => {
      Object.entries(updateData).forEach(([key, value]) => {
        setValue(key, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      })
      handleRecurrenceUpdated()
    },
    [setValue, handleRecurrenceUpdated]
  )

  const fieldSize = { xs: 12, lg: 6 }

  const billingNameToggle = (
    <HHFormMuiSwitch
      control={control}
      disabled={!canEdit}
      label={T.DIFFERENT_BILLING_NAME_LABEL}
      labelPlacement="start"
      name="useBillingName"
      sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
      onChange={onUseBillingNameChange}
    />
  )

  // Recurring pairs the toggle with "Different billing dates" in the Billing header; on-request has no
  // sibling toggle, so it stays inline.
  const billingNameField = (inlineToggle: boolean) => (
    <Grid size={12}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <HHFormTextField
            fullWidth
            control={control}
            deprecatedLabel={false}
            disabled={!canEdit || !useBillingName}
            label={capitalize(T.BILLING_NAME)}
            name="billingName"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 1 }}>
          <Tooltip arrow title={T.BILLING_NAME_INFO}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Tooltip>
          {inlineToggle && billingNameToggle}
        </Box>
      </Box>
    </Grid>
  )

  return (
    <Box sx={{ p: 3, pb: 4 }}>
      <Grid container spacing={3}>
        {/* LEFT COLUMN */}
        <Grid size={{ xs: 12, lg: isOnRequestService ? 12 : 6 }}>
          <Grid container spacing={2}>
            {isRecurringService && (
              <>
                <AdjustServiceSectionHeader title={T.SERVICE}>
                  <ServiceActiveSwitch
                    control={control}
                    disabled={!canEdit}
                    error={Boolean(deactivateError)}
                    helperText={deactivateError}
                    rules={{ validate: validateDeactivateField }}
                    onChange={handleDeactivateChange}
                  />
                </AdjustServiceSectionHeader>
                <Grid size={fieldSize}>
                  <HHFormDesktopDatePicker
                    fullWidth
                    control={control}
                    disabled={!canEdit}
                    error={startDateError}
                    helperText={startDateError}
                    label={capitalize(T.START_DATE)}
                    name="startDate"
                    rules={{ validate: validateStartDateField }}
                    shouldDisableDate={shouldDisableStartDate}
                    TextFieldProps={{ deprecatedLabel: false, clearable: canEdit, error: startDateError, helperText: startDateError }}
                  />
                </Grid>
                <Grid size={fieldSize}>
                  <HHFormDesktopDatePicker
                    fullWidth
                    control={control}
                    disabled={!canEdit}
                    label={capitalize(T.END_DATE)}
                    name="endDate"
                    shouldDisableDate={shouldDisableEndDate}
                    TextFieldProps={{ deprecatedLabel: false, clearable: canEdit }}
                  />
                </Grid>
                <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <HHFormMuiSwitch
                    control={control}
                    disabled={!canEdit}
                    label={T.DEACTIVATE_ON_END_DATE_LABEL}
                    labelPlacement="start"
                    name="deactivateOnEndDate"
                    sx={{ color: 'text.secondary' }}
                    onChange={handleDeactivateOnEndDateChange}
                  />
                </Grid>

                <AdjustServiceSectionHeader title={T.BILLING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 2 }}>
                    {billingNameToggle}
                    <HHFormMuiSwitch
                      control={control}
                      disabled={!canEdit}
                      label={T.DIFFERENT_BILLING_DATES_LABEL}
                      labelPlacement="start"
                      name="useDifferentDatesForBilling"
                      sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                      onChange={onDifferentBillingDatesChange}
                    />
                  </Box>
                </AdjustServiceSectionHeader>
                {billingNameField(false)}
                {useDifferentDatesForBilling ? (
                  <>
                    <Grid size={fieldSize}>
                      <HHFormDesktopDatePicker
                        fullWidth
                        control={control}
                        disabled={!canEdit}
                        label={capitalize(T.BILLING_START_DATE)}
                        name="billingStartDate"
                        shouldDisableDate={shouldDisableBillingStartDate}
                        TextFieldProps={{ deprecatedLabel: false }}
                      />
                    </Grid>
                    <Grid size={fieldSize}>
                      <HHFormDesktopDatePicker
                        fullWidth
                        control={control}
                        disabled={!canEdit}
                        label={capitalize(T.BILLING_END_DATE)}
                        name="billingEndDate"
                        shouldDisableDate={shouldDisableBillingEndDate}
                        TextFieldProps={{ deprecatedLabel: false }}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid size={fieldSize}>
                      <HHDesktopDatePicker
                        label={capitalize(T.BILLING_START_DATE)}
                        TextFieldProps={{ deprecatedLabel: false, disabled: true }}
                        value={watchedStartDate}
                      />
                    </Grid>
                    <Grid size={fieldSize}>
                      <HHDesktopDatePicker
                        label={capitalize(T.BILLING_END_DATE)}
                        TextFieldProps={{ deprecatedLabel: false, disabled: true }}
                        value={watchedEndDate}
                      />
                    </Grid>
                  </>
                )}

                <AdjustServiceSectionHeader title={T.SERVICE_SCHEDULE} />
                {showRecurrenceEditAlert && (
                  <Grid size={12}>
                    <HHAlert
                      borderColor={theme.palette.warning.dark}
                      severity="warning"
                      sx={{ bgcolor: alpha(theme.palette.warning.main, 0.08) }}
                    >
                      {recurrencePublishBlockerMessage}
                    </HHAlert>
                  </Grid>
                )}
                <Grid size={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <HHFormSelectField
                      disabled
                      fullWidth
                      control={control}
                      deprecatedLabel={false}
                      getOptionLabel={getSimpleOptionLabel}
                      getOptionValue={getSimpleOptionValue}
                      label={capitalize(T.FREQUENCY)}
                      name="recurrenceFrequency"
                      options={RECURRENCE_INTERVALS}
                    />
                    <HHFormAutocompleteField
                      disabled
                      fullWidth
                      control={control}
                      deprecatedLabel={false}
                      getOptionLabel={getRecurrenceOptionLabel}
                      isOptionEqualToValue={isRecurrenceOptionEqual}
                      label={capitalize(T.REPEATS)}
                      name="recurrence"
                      options={recurrenceList}
                    />
                    <Tooltip arrow title={T.CHANGING_RECURRENCE_DOES_NOT_UPDATE_PRICING}>
                      <InfoOutlinedIcon sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                    {canEdit && (
                      <EditServiceRecurrence
                        metaFiltersData={metaFiltersData}
                        serviceId={serviceId}
                        onRecurrenceUpdate={handleRecurrenceUpdate}
                      />
                    )}
                  </Box>
                </Grid>
                {isOnRequestService && <ServiceDetailsViewItem icon={<RecurrenceIcon />} primary={recurrenceText} size={12} />}
                {isRecurringService && <RecurrenceDisplayList data={data} watchedRecurrence={watchedRecurrence} />}
              </>
            )}

            {!isRecurringService && (
              <>
                <Grid size={12}>
                  <ServiceActiveSwitch
                    control={control}
                    disabled={!canEdit}
                    error={Boolean(deactivateError)}
                    helperText={deactivateError}
                    rules={{ validate: validateDeactivateField }}
                    onChange={handleDeactivateChange}
                  />
                </Grid>
                {isOnRequestService && (
                  <>
                    <AdjustServiceSectionHeader title={T.SERVICE_ATTRIBUTES} />
                    <ServiceAttributeFields
                      actionList={actionList}
                      canEdit={canEdit}
                      control={control}
                      fieldSize={fieldSize}
                      materialList={materialList}
                      measureList={measureList}
                      methodList={methodList}
                    />
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Grid container spacing={2}>
            {!isOnRequestService && (
              <>
                <AdjustServiceSectionHeader title={T.SERVICE_ATTRIBUTES} />
                <ServiceAttributeFields
                  actionList={actionList}
                  canEdit={canEdit}
                  control={control}
                  fieldSize={fieldSize}
                  materialList={materialList}
                  measureList={measureList}
                  methodList={methodList}
                />
              </>
            )}

            <AdjustServiceSectionHeader title={T.PRICING} />
            {!isRecurringService && billingNameField(true)}
            <Grid size={fieldSize}>
              <HHFormAutocompleteField
                control={control}
                deprecatedLabel={false}
                disabled={!canEdit}
                getOptionLabel={getPricingLabel}
                label={capitalize(T.PRICING_PERIOD)}
                name="pricingPeriod"
                options={pricingPeriodList}
              />
            </Grid>
            <Grid size={fieldSize}>
              <PricingZoneAutocompleteFormField control={control} disabled={!canEdit} name="pricingZone" options={pricingZoneList} />
            </Grid>
            <Grid size={fieldSize}>
              <HHFormTextField
                fullWidth
                control={control}
                deprecatedLabel={false}
                disabled={!canEdit}
                label={capitalize(T.PURCHASE_ORDER_LABEL)}
                name="purchaseOrderNumber"
              />
            </Grid>
            <Grid size={fieldSize}>
              <HHFormPriceField
                fullWidth
                control={control}
                deprecatedLabel={false}
                disabled={!canEditPrice}
                label={capitalize(T.PRICE)}
                name="price"
              />
            </Grid>
            {isLegacyTaxesEnabled && (
              <Grid size={fieldSize}>
                <HHFormAutocompleteField
                  fullWidth
                  control={control}
                  deprecatedLabel={false}
                  disabled={!canEdit}
                  getOptionLabel={getTaxZoneOptionLabel}
                  label={capitalize(isNewTaxesEnabled ? T.LEGACY_TAX_ZONE : T.TAX_ZONE)}
                  name="taxZone"
                  options={taxZoneList}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

const AdjustServicePanel = ({ serviceId }: { serviceId: string }) => {
  const { isOpen, methods } = useAdjustService() as { isOpen: boolean; methods: UseFormReturn<FieldValues> }

  return (
    <Collapse unmountOnExit in={isOpen} sx={{ '&.MuiCollapse-entered': { overflow: 'visible' } }}>
      <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <FormProvider {...methods}>
          <AdjustServiceFormContent serviceId={serviceId} />
        </FormProvider>
      </Box>
    </Collapse>
  )
}

export default AdjustServicePanel
